import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useTranslation } from 'react-i18next'
import { LIVE_POSITION_EVERY_S, type WorkReportPositionV1 } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { usePageVisible } from '../ui/hooks.ts'
import { useMyWork } from './work.ts'
import { sweepTransient } from '../sync/transient.ts'
import { REFUSED_STATUSES } from '../sync/lists.ts'
import { getMeta, setMeta, type GkDb, type OutboxRow } from '../sync/db.ts'
import { BACKGROUND_GEOLOCATION, hasNativePlugin, nativePlatform } from '../platform/native.ts'
import type { NativeWatch } from '../platform/nativePosition.ts'
import { onBackOnScreen } from '../platform/visibility.ts'
import { useLocationOn } from './location.ts'

/** Sharing one's position with the team (M19, ADR-110). A phone shares its person's position ONLY while one of their work sessions is RUNNING (between
 *  a start or a resume and a pause or a finish), only while the app is on screen and the engine KNOWS it is online (an unknown state, before the first
 *  answer, is not online), and never on a shared workstation. A position is taken with ONE `getCurrentPosition` at a time (at once when a timer starts
 *  or resumes, then every LIVE_POSITION_EVERY_S): `watchPosition` stays banned. It is sent as `work.report_position@2` (POSITION_COMMAND; queued, never waited for) with
 *  the phone's coordinates as given (never rounded: the command log keeps a hash of the real payload, and rounded coordinates would make it guessable)
 *  and the instant of the fix on the SERVER's clock (the fix's own timestamp minus the engine's measured offset, as the dispatcher corrects a fact's
 *  occurred_at). The server keeps one current position per running session and clears it at the pause or the finish, so nothing outlives the session.
 *  This device keeps even less: a position is written nowhere but the outbox row that carries it, and that row goes as soon as the server has answered
 *  (sync/transient.ts: deleted when applied, left with the session id only when refused); a row still waiting for a session that is no longer running
 *  (paused, finished, or the app reopened later) is dropped, since the server would ignore it.
 *  A REFUSAL (rejected, conflict, error) stops the sharing of that session on this device for the stretch of work that runs (its open interval): no
 *  refused row every three minutes. A resume opens a new stretch and a new task another session, and those are tried again. The refused stretches are
 *  remembered on the device (ids only), so a refusal dismissed on the sync page does not start the sending again.
 *  The rules of WHEN are pure functions below, with their tests; the hook only wires them to the browser.
 *
 *  THE NATIVE APP (M19b, ADR-111) follows the same rules with one difference, the owner's: the page being hidden (the phone in a pocket, the screen
 *  off, the app in the background) does NOT stop the sharing. Its positions come from the background-geolocation plugin (platform/nativePosition.ts,
 *  loaded only there): ONE watcher, added when one of the person's sessions starts running on this device and REMOVED at once on a pause, a finish,
 *  a sign-out (the layout goes), on a workstation, after a refusal of the server or of the phone. Every position the plugin gives is not sent: one
 *  `work.report_position@2` at the start or the resume, then one every LIVE_POSITION_EVERY_S (the same `nextFixIn`), with the fix's own time on the
 *  server's clock, and the engine's sync is called at once (a background app's timers are throttled; the plugin's positions are what wakes it). A
 *  network loss does not remove the watcher (nothing would wake the app again); nothing is queued while the engine is not online: the due position
 *  first asks the engine to sync, and is queued only once the server answered. The browser keeps its one-shot fixes, and never `watchPosition`.
 *  The engine's clock offset is read once per watcher and kept in memory, and a fix older than one period when it would be queued is dropped: a page
 *  frozen in the background (Android's web view, M19b) holds the plugin's positions back, and one of them must never be sent as the current one.
 *
 *  ONLY THE STRETCHES THIS DEVICE OPENED are shared, in both paths (`openedHere`): a person may have the app on two devices (a phone and a tablet,
 *  the iPhone and an Android phone), and the session that runs is shared by the device that started or resumed it, never by the other one too. The
 *  server holds the same rule (`work.report_position@2`, migration 20261115100300): a report from another device than the one that opened the running
 *  stretch is applied and stores nothing.
 *
 *  NONE OF IT happens where the club has location OFF (M21, the owner's answer of 2026-10-23; the site's setting, `data/location.ts`, off by default):
 *  no fix is asked of the browser, no native watcher is added (so the phone never asks its person either), nothing is said and nothing waits in the
 *  outbox. The server refuses a position for such a site whatever sends it (LOCATION_OFF, migration 20261123100100). */

export type ShareState = 'off' | 'sharing' | 'denied' | 'unavailable' | 'refused'
type FixResult = 'ok' | 'denied' | 'unavailable'

export interface ShareConditions {
  /** the site's location setting is on (M21, data/location.ts): off, nothing is ever asked of the phone, said or sent, whatever else holds */
  location: boolean
  /** the person signed in on this device (null on a workstation, or before the club is known) */
  personId: string | null
  kiosk: boolean
  /** the person may do field work here (cosmetic: the server decides) and the device can queue commands */
  permitted: boolean
  /** ids of the person's sessions that are running now (an open timer interval) */
  running: readonly string[]
  /** of those, the sessions whose position the server refused during the stretch that runs now: nothing more is sent for them until a resume */
  refused: readonly string[]
  visible: boolean
  /** the engine's last word was "online" (`EngineState.online === true`): null (no answer yet) and false are both NOT online */
  online: boolean
  /** a position source: `navigator.geolocation` in a browser, the background-geolocation plugin in the native app */
  geolocation: boolean
  /** the native app (M19b): a hidden page and a network loss do not stop its watcher (the browser stops at once) */
  native?: boolean
}

/** The engine's network state as the sharing reads it: only a known "online" counts; null (no answer yet, e.g. at start-up) is NOT online. */
export const engineOnline = (online: boolean | null): boolean => online === true

/** The running sessions a position is sent for: every running one the server has not refused in this stretch. */
export const sharedSessions = (c: Pick<ShareConditions, 'running' | 'refused'>): string[] => c.running.filter((s) => !c.refused.includes(s))

/** Was this stretch of work (an open interval) opened by THIS device? Only those are shared. The server's row carries the device that sent its start or
 *  its resume (the provenance `device_id`, never changed afterwards); this device's own pending start or resume is a projection that carries this
 *  device's id too (sync/projectors.ts), and an older build's projection, without the field, is this device's by definition (the overlay holds only
 *  this device's commands). The device id is the device store's own (one per person and club on a device): it survives a reload and a new sign-in of
 *  the same person, so a stretch opened before either is still this device's. A stretch opened on another device is never this device's to share. */
export function openedHere(stretch: { device_id?: string | null; _pending?: boolean }, deviceId: string | null | undefined): boolean {
  if (!deviceId) return false
  if (stretch.device_id === deviceId) return true
  return stretch.device_id === undefined && stretch._pending === true
}

/** Take positions now? Every condition must hold; any one failing stops the sharing at once. In the native app, the page on screen and the network
 *  are not conditions of the watcher (what is SENT still waits for the network: `nativeReport`). */
export function shareWanted(c: ShareConditions): boolean {
  const mine = c.location && !!c.personId && !c.kiosk && c.permitted && sharedSessions(c).length > 0 && c.geolocation
  return c.native ? mine : mine && c.visible && c.online
}

/** What the person is told (the banner on Today and on the work page). Nothing is said when location is off at the site, nothing runs, offline or with
 *  the page hidden. */
export function shareStateOf(c: ShareConditions, last: FixResult | null): ShareState {
  if (!c.location || !c.personId || c.kiosk || !c.permitted || c.running.length === 0) return 'off'
  if (sharedSessions(c).length === 0) return 'refused'
  if (!c.geolocation) return 'unavailable'
  if (!c.online || (!c.visible && !c.native)) return 'off'
  return last === 'denied' ? 'denied' : last === 'unavailable' ? 'unavailable' : 'sharing'
}

/** What is running, as one key: a start or a resume opens a NEW interval, so the key changes and a position is due at once. */
export const runningKey = (openIntervalIds: readonly string[]): string => [...openIntervalIds].sort().join(',')

/** Milliseconds until the next position is due: now when nothing was taken for what runs now (a start, a resume), otherwise one period after the last
 *  attempt (successful or not: a phone that refuses is not asked again and again). */
export function nextFixIn(last: { at: number; key: string } | null, key: string, now: number, everyMs = LIVE_POSITION_EVERY_S * 1000): number {
  if (!last || last.key !== key) return 0
  return Math.max(0, last.at + everyMs - now)
}

export interface Fix { lat: number; lon: number; accuracy: number; timestamp: number }
/** The commands for one position: one per session it is sent for (the server keeps one position per session). `clockOffsetMs` is the engine's measured
 *  offset (device minus server, `getMeta(db, 'clockOffsetMs')`): `at` is the fix's own timestamp on the server's clock. The coordinates and the
 *  accuracy go as the phone gave them (never rounded). Null when the phone's answer is unusable (not a number, out of range, or an accuracy worse than
 *  ten kilometres): nothing is sent then. */
export function positionPayloads(sessionIds: readonly string[], fix: Fix, clockOffsetMs = 0): WorkReportPositionV1[] | null {
  const { lat, lon, accuracy, timestamp } = fix
  const offset = Number.isFinite(clockOffsetMs) ? clockOffsetMs : 0
  if (![lat, lon, accuracy, timestamp].every(Number.isFinite)) return null
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180 || accuracy < 0 || accuracy > 10_000) return null
  const at = new Date(timestamp - offset).toISOString()
  return sessionIds.map((session_id) => ({ session_id, lat, lon, accuracy_m: accuracy, at }))
}

/** The outbox's double-tap guard keyed by session: while a position of a session is still waiting to be sent, a newer one is NOT queued beside it. */
export const positionIntentKey = (sessionId: string): string => `position|${sessionId}`

/** GeolocationPositionError: 1 = the person or the phone refused; 2 and 3 (no fix, too slow) = unavailable. */
export const geoErrorState = (code: number): 'denied' | 'unavailable' => (code === 1 ? 'denied' : 'unavailable')

/** A waiting position row is still wanted only while its session runs (the sweep drops the others). */
export const positionStillWanted = (running: ReadonlySet<string>) => (row: Pick<OutboxRow, 'payload'>): boolean =>
  running.has(String((row.payload as { session_id?: unknown }).session_id ?? ''))

const POSITION = 'work.report_position'
/** The version this app sends (M19b): @2 is @1 plus the server's own one-device rule (only the device that opened the running stretch shares it, the
 *  rule `openedHere` already follows here); @1 stays for an older build. */
export const POSITION_COMMAND = 'work.report_position@2' as const
const REFUSED: ReadonlySet<string> = new Set(REFUSED_STATUSES)
/** Where the device remembers the stretches whose positions were refused (interval ids, nothing else), once the refusal itself is dismissed. */
export const REFUSED_STRETCHES_META = 'positionRefusedStretches'

/** The stretches of work running now (open interval ids) whose position the server refused: from the refused position rows still on the device (each
 *  carries, in `local_tag`, the interval it was taken in) and from the stretches remembered after such a refusal was dismissed. Only stretches that still
 *  run are returned, sorted: a resume opens a new interval and a new task another session, and those are tried again. */
export function refusedStretches(rows: ReadonlyArray<Pick<OutboxRow, 'name' | 'status' | 'local_tag'>>, remembered: readonly string[], runningIntervals: readonly string[]): string[] {
  const run = new Set(runningIntervals)
  const out = new Set<string>()
  for (const r of rows) if (r.name === POSITION && REFUSED.has(r.status) && r.local_tag && run.has(r.local_tag)) out.add(r.local_tag)
  for (const i of remembered) if (run.has(i)) out.add(i)
  return [...out].sort()
}

export const GEO_OPTIONS: PositionOptions = { enableHighAccuracy: true, maximumAge: 30_000, timeout: 20_000 }

/** The native app retries a watcher that failed for another reason than a refusal (e.g. Android's location service not yet bound at start-up). */
export const NATIVE_RETRY_MS = 15_000

/** The engine as the native reporting needs it: its last known network state and a sync it can call directly. */
export interface SyncNow { readonly state: { online: boolean | null }; sync(o?: { force?: boolean }): Promise<unknown> }

/** Push what was just queued NOW (a background app's timers are throttled, so the scheduler's "soon" may come late): one forced cycle, and a second
 *  one when a row is still queued (a cycle already running when it was queued took the outbox before it). */
export async function syncNow(engine: SyncNow, stillQueued: () => Promise<boolean>): Promise<void> {
  await engine.sync({ force: true }).catch(() => undefined)
  if (await stillQueued().catch(() => false)) await engine.sync({ force: true }).catch(() => undefined)
}

/** Is this fix older than one period (its own time, on the device's clock)? Such a fix was held back on its way (a page frozen in the background, a
 *  slow sync): it is not the current position, and it is never sent as one. */
export const fixTooOld = (fix: Pick<Fix, 'timestamp'>, now: number, everyMs = LIVE_POSITION_EVERY_S * 1000): boolean => now - fix.timestamp > everyMs

/** One position from the native watcher. Returns what happened: 'throttled' (not due: one per LIVE_POSITION_EVERY_S after the start or the resume),
 *  'offline' (due, but the engine is not online even after a sync: nothing is queued), 'unusable' (not a position), 'stale' (older than one period
 *  when it arrived or when it would be queued: dropped, and the next fix is due at once), or 'sent' (queued, then pushed).
 *  `last` is the same memory as the browser's (the last attempt and the stretches it was for): a due position counts as an attempt even when it could
 *  not be sent, so an offline phone asks the engine once a period, not once a fix. `clockOffset` is the watcher's own memory of the engine's offset
 *  (never a read of the device store per fix); `clock` is the device's clock (Date.now), read again just before queuing, and `send` gets the same
 *  check (`fresh`) to make inside the write itself, where a page frozen meanwhile would otherwise still write an old fix. */
export async function nativeReport(fix: Fix, d: {
  key: string; targets: ReadonlyArray<readonly [session: string, stretch: string]>; last: { current: { at: number; key: string } | null }; now: number
  engine: SyncNow; clockOffset(): Promise<number | undefined> | number | undefined
  send(p: WorkReportPositionV1, stretch: string, fresh: () => boolean): Promise<string | null>
  stillQueued(ids: string[]): Promise<boolean>
  stopped(): boolean
  clock?(): number
}): Promise<'throttled' | 'offline' | 'unusable' | 'stale' | 'sent' | 'stopped'> {
  if (d.stopped()) return 'stopped'
  if (fixTooOld(fix, d.now)) return 'stale'
  if (nextFixIn(d.last.current, d.key, d.now) > 0) return 'throttled'
  const before = d.last.current
  const attempt = { at: d.now, key: d.key }
  d.last.current = attempt
  if (!engineOnline(d.engine.state.online)) {
    await d.engine.sync({ force: true }).catch(() => undefined)
    if (d.stopped()) return 'stopped'
    if (!engineOnline(d.engine.state.online)) return 'offline'
  }
  const offset = await Promise.resolve(d.clockOffset()).catch(() => undefined)
  const payloads = positionPayloads(d.targets.map(([s]) => s), fix, offset ?? 0)
  if (!payloads) return 'unusable'
  if (d.stopped()) return 'stopped'
  const clock = d.clock ?? Date.now
  const fresh = () => !fixTooOld(fix, clock())
  const dropped = () => { if (d.last.current === attempt) d.last.current = before; return 'stale' as const }   // not an attempt: the next fix is due at once
  if (!fresh()) return dropped()
  const ids = (await Promise.all(payloads.map((p, n) => d.send(p, d.targets[n]![1], fresh).catch(() => null)))).filter((x): x is string => !!x)
  if (ids.length === 0 && !fresh()) return dropped()
  await syncNow(d.engine, () => d.stillQueued(ids))
  return 'sent'
}

const NO_JOBS: never[] = []
const NO_ROWS: OutboxRow[] = []

/** What the layout hands down: the state to tell, the running sessions not shared because the server refused them in this stretch, and the person's
 *  sessions running in a stretch another device opened (that device shares them; this one says nothing about them). */
export interface LiveShare { state: ShareState; refused: readonly string[]; elsewhere?: readonly string[] }

/** Used ONCE, in the layout: shares the signed-in person's position while one of their sessions runs. Returns what to tell them. */
export function useShareMyPosition(): LiveShare {
  const { t } = useTranslation()
  const { personId, kiosk, send, can, platform, db, engine, deviceId } = useClub()
  const online = engineOnline(useEngineState().online)
  const visible = usePageVisible()
  const native = nativePlatform()
  // M21: the site's setting first; off (or not known yet), the phone is never asked and nothing is queued
  const location = useLocationOn()
  const work = useMyWork(NO_JOBS)
  // the running sessions and the stretch (open interval) each one is in: only the stretches THIS device opened (another device of the person shares
  // its own); the others are only named, so that their work page says nothing here
  const pairs = work.mine.filter((w) => w.running && openedHere(w.running, deviceId)).map((w) => [w.session.id, w.running!.id] as const)
  const elsewhereKey = work.mine.filter((w) => w.running && !openedHere(w.running, deviceId)).map((w) => w.session.id).sort().join(',')
  const sessionsKey = pairs.map(([s]) => s).sort().join(',')
  const key = runningKey(pairs.map(([, i]) => i))

  // refusals: the refused position rows of this device, and the stretches remembered once their refusal was dismissed
  const refusedRows = useLiveQuery(() => db.outbox.where('status').anyOf([...REFUSED_STATUSES]).filter((r) => r.name === POSITION).toArray(), [db], NO_ROWS)
  const remembered = useLiveQuery(async () => (await getMeta<string[]>(db, REFUSED_STRETCHES_META)) ?? [], [db], null)
  const stretches = refusedStretches(refusedRows, remembered ?? [], pairs.map(([, i]) => i))
  const stretchesKey = stretches.join(',')
  const refused = pairs.filter(([, i]) => stretches.includes(i)).map(([s]) => s).sort()
  const sendKey = pairs.filter(([s]) => !refused.includes(s)).map(([s, i]) => `${s}:${i}`).sort().join(',')
  useEffect(() => {
    // keep exactly the refused stretches that still run (a pause, a finish or a resume lets the memory go); only once the work is known
    if (!work.ready || kiosk || remembered === null) return
    if (stretchesKey !== [...remembered].sort().join(',')) void setMeta(db, REFUSED_STRETCHES_META, stretchesKey ? stretchesKey.split(',') : []).catch(() => undefined)
  }, [db, work.ready, kiosk, remembered, stretchesKey])

  const cond: ShareConditions = {
    location, personId, kiosk: !!kiosk, permitted: can('job.execute') && platform.mode !== 'online_only', running: sessionsKey ? sessionsKey.split(',') : [],
    refused, visible, online, native: !!native,
    geolocation: native ? hasNativePlugin(BACKGROUND_GEOLOCATION) : typeof navigator !== 'undefined' && !!navigator.geolocation,
  }
  const wanted = shareWanted(cond)

  // what this device still holds of positions: nothing answered, and nothing waiting for a session that no longer runs (at every start, pause,
  // resume and finish, and once when the app opens; the engine's own start-up sweep has already taken what an older build left); with location off at the
  // site, none is wanted: every waiting position goes (the server would refuse it, LOCATION_OFF)
  useEffect(() => {
    if (!work.ready || kiosk) return
    void sweepTransient(db, positionStillWanted(new Set(location && sessionsKey ? sessionsKey.split(',') : []))).catch(() => undefined)
  }, [db, work.ready, kiosk, sessionsKey, location])
  const [result, setResult] = useState<FixResult | null>(null)
  const last = useRef<{ at: number; key: string } | null>(null)

  useEffect(() => {
    if (!wanted || native) return
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const targets = sendKey.split(',').map((x) => x.split(':') as [string, string])     // [session, the stretch it is in]
    const schedule = () => { if (!stopped) timer = setTimeout(take, nextFixIn(last.current, key, Date.now())) }
    function take() {
      if (stopped) return
      last.current = { at: Date.now(), key }
      navigator.geolocation.getCurrentPosition((pos) => {
        if (stopped) return                                   // paused, finished, hidden, offline or refused meanwhile: this answer is dropped, never sent
        const fix: Fix = { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy, timestamp: pos.timestamp }
        void getMeta<number>(db, 'clockOffsetMs').catch(() => undefined).then((offset) => {
          if (stopped) return
          const payloads = positionPayloads(targets.map(([s]) => s), fix, offset ?? 0)
          if (!payloads) { setResult('unavailable'); schedule(); return }
          payloads.forEach((p, n) => {
            void send(POSITION_COMMAND, p, { intentKey: positionIntentKey(p.session_id), localTag: targets[n]![1] }).catch(() => undefined)
          })
          setResult('ok'); schedule()
        })
      }, (err) => { if (stopped) return; setResult(geoErrorState(err.code)); schedule() }, GEO_OPTIONS)
    }
    schedule()
    return () => { stopped = true; clearTimeout(timer) }
  }, [wanted, native, key, sendKey, send, db])

  // the native app: ONE watcher while something of the person's runs (removed by this cleanup at a pause, a finish, a refusal, a sign-out); a refusal
  // of the phone is tried again when the person comes back to the app (from the phone's settings), another failure after NATIVE_RETRY_MS
  const [attempt, setAttempt] = useState(0)
  const title = t('live.native.title'), message = t('live.native.message')
  useEffect(() => {
    if (!wanted || !native) return
    let stopped = false
    let watch: NativeWatch | null = null
    let retry: ReturnType<typeof setTimeout> | undefined
    let unwatchScreen: (() => void) | null = null
    // the engine's clock offset, read ONCE for this watcher and kept here (read again after each push, which measured it anew): a fix never waits on
    // the device store, which a page frozen in the background leaves pending (and the fix with it)
    let offset: Promise<number | undefined> = getMeta<number>(db, 'clockOffsetMs').catch(() => undefined)
    const refreshOffset = () => { void getMeta<number>(db, 'clockOffsetMs').then((v) => { if (v !== undefined) offset = Promise.resolve(v) }).catch(() => undefined) }
    const targets = sendKey.split(',').map((x) => x.split(':') as [string, string])     // [session, the stretch it is in]
    const report = (fix: Fix) => void nativeReport(fix, {
      key, targets, last, now: Date.now(), engine, stopped: () => stopped,
      clockOffset: () => offset,
      send: async (p, stretch, fresh) => (await send(POSITION_COMMAND, p, {
        intentKey: positionIntentKey(p.session_id), localTag: stretch,
        // the last check, inside the write itself: a fix that got older than one period on its way (the page frozen meanwhile) is not written at all
        alsoWrite: async () => { if (!fresh()) throw new Error('POSITION_TOO_OLD') },
      })).commandId,
      stillQueued: (ids) => anyStillQueued(db, ids),
    }).then((r) => {
      if (stopped) return
      if (r === 'sent') refreshOffset()
      if (r === 'sent' || r === 'unusable') setResult(r === 'sent' ? 'ok' : 'unavailable')
    }).catch(() => undefined)
    void import('../platform/nativePosition.ts').then((m) => {
      if (stopped) return                                    // paused, finished, signed out or refused while the module loaded: no watcher at all
      watch = m.startNativeWatch({
        platform: native, title, message, onFix: report,
        onError: (e) => {
          if (stopped) return
          setResult(e)
          // a refusal of the phone: tried again once the person has been away from the app (the phone's settings) and is back on screen
          if (e === 'denied') unwatchScreen ??= onBackOnScreen(() => setAttempt((a) => a + 1))
          else retry = setTimeout(() => setAttempt((a) => a + 1), NATIVE_RETRY_MS)
        },
      })
    }).catch(() => { if (!stopped) setResult('unavailable') })     // the module failed to load
    return () => { stopped = true; clearTimeout(retry); unwatchScreen?.(); void watch?.stop() }
  }, [wanted, native, key, sendKey, send, db, engine, title, message, attempt])

  const state = shareStateOf(cond, result)
  // M21: off at the site, nothing is said for any session either (a refusal still held, LOCATION_OFF among them, keeps its memory for a later 'on')
  const refusedKey = location ? refused.join(',') : ''
  return useMemo(() => ({ state, refused: refusedKey ? refusedKey.split(',') : [], elsewhere: elsewhereKey ? elsewhereKey.split(',') : [] }), [state, refusedKey, elsewhereKey])
}

/** Is one of these outbox rows still waiting to be sent? */
async function anyStillQueued(db: GkDb, ids: string[]): Promise<boolean> {
  if (ids.length === 0) return false
  return (await db.outbox.bulkGet(ids)).some((r) => r?.status === 'queued')
}

/** What the banner says for one session (the work page) or for all of the person's running ones (Today, `sessionId` left out). A session running in
 *  a stretch another device opened is that device's to share: nothing is said about it here. */
export const shareStateFor = (share: LiveShare, sessionId?: string): ShareState =>
  sessionId && share.elsewhere?.includes(sessionId) ? 'off' : sessionId && share.refused.includes(sessionId) ? 'refused' : share.state

/** The layout hands the sharing state down to the pages that show it. */
export const LiveShareCtx = createContext<LiveShare>({ state: 'off', refused: [] })
export const useLiveShare = (): LiveShare => useContext(LiveShareCtx)
